import * as React from 'react';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputFile,
	UseInputFileResults,
	UseInputFormResult,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	FormPopupResubmitProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	FileExtension,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';
// Import app models.
import {
	FileData,
} from '@app/models/app';
// Import api models.
import {
	DocumentUpload,
} from '@app/models/api';

interface UseFormPopupResubmitParameters {
	id: string;
	props: FormPopupResubmitProps,
}

interface UseFormPopupResubmitResults {
	submitForm: () => void;
	noteInput: UseInputTextResults;
	fileInput: UseInputFileResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupResubmit = (params: UseFormPopupResubmitParameters): UseFormPopupResubmitResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// If form passes validation, proceed to submission.
		if (isValid) {
			params.props.onSave(noteInput.value, createDocumentUpload());
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Resubmission',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Resubmit',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	const createDocumentUpload = (): DocumentUpload | undefined => {
		let documentUpload: DocumentUpload | undefined = undefined;
		if (!String.isNullOrWhiteSpace(fileInput.value.fileName)
			&& fileInput.value.fileInput !== undefined) {
			documentUpload = new DocumentUpload({
				docName: fileInput.value.fileName,
				docData: fileInput.value.fileInput.toUint8Array(),
			});
		}
		return documentUpload;
	}

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<string>(noteInput)
			&& checkInputValidationWithFocus<FileData>(fileInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		noteInput.changeShowValidationFlag(true);
		fileInput.changeShowValidationFlag(true);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for note. 
	 **/
	const generateIdForNote = (): string => {
		return `${generateIdForForm()}-note`;
	}

	/**
	 * Generate id for file. 
	 **/
	const generateIdForFile = (): string => {
		return `${generateIdForForm()}-file`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	let allowedFileExtensions: FileExtension[] = [
		FileExtension.ZIP,
		FileExtension.PDF,
		FileExtension.TXT,
		FileExtension.GIF,
		FileExtension.JPG,
		FileExtension.PNG,
	];

	// Instantiate the 'note' input hook.
	const noteInput: UseInputTextResults = useInputText({
		id: generateIdForNote(),
		label: 'Note',
		required: true,
		disabled: false,
		value: String.empty(),
		showValidation: false,
	});

	// Instantiate the 'file' input hook.
	const fileInput: UseInputFileResults = useInputFile({
		id: generateIdForFile(),
		label: `(${allowedFileExtensions.join(', ')})`,
		required: false,
		disabled: false,
		file: new FileData({
			fileName: String.empty(),
			fileInput: undefined,
		}),
		allowedFileExtensions: allowedFileExtensions,
		showValidation: false,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// Initially the first input should be focused.
		noteInput.focusToInput();
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		noteInput: noteInput,
		fileInput: fileInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupResubmitParameters,
}

// Export results.
export {
	UseFormPopupResubmitResults,
}

export default useFormPopupResubmit;